import React, { useState, useEffect, useContext } from 'react';
import { useSelector } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { useTranslation } from "react-i18next";
import { FirebaseContext } from 'common';
import { ref, onValue, set } from "firebase/database";
import {
  Box, Card, CardContent, Typography, TextField, Button, Grid,
  Switch, FormControlLabel, Divider, Chip, IconButton, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Select, InputLabel, FormControl, CircularProgress
} from '@mui/material';
import { Add, Delete, Edit, Save, EmojiEvents, Star } from '@mui/icons-material';
import { FONT_FAMILY, WEB_MAIN_COLOR, WEB_SECONDORY_COLOR } from "../common/sharedFunctions";

const DEFAULT_XP_CONFIG = {
  onlinePerHour: 10,
  rideComplete: 20,
  acceptRateBonus: {
    threshold: 95,
    xp: 15
  },
  fiveStarRating: 5,
  enabled: true
};

const DEFAULT_LEVELS = [
  { name: 'Bronze', minXP: 0, icon: '🥉', color: '#CD7F32' },
  { name: 'Silver', minXP: 500, icon: '🥈', color: '#C0C0C0' },
  { name: 'Gold', minXP: 1500, icon: '🥇', color: '#FFD700' },
  { name: 'Platinum', minXP: 3500, icon: '💎', color: '#E5E4E2' },
  { name: 'Diamond', minXP: 7000, icon: '👑', color: '#B9F2FF' },
];

const REWARD_TYPES = [
  { value: 'fuel_voucher', label: 'Fuel Voucher' },
  { value: 'gcash_credit', label: 'GCash Credit' },
  { value: 'free_commission', label: 'Free Commission Day' },
  { value: 'priority_booking', label: 'Priority Bookings' },
  { value: 'cash_bonus', label: 'Cash Bonus' },
  { value: 'custom', label: 'Custom Reward' },
];

export default function LeagueSettings() {
  const { t } = useTranslation();
  const settings = useSelector((state) => state.settingsdata.settings);
  const firebase = useContext(FirebaseContext);
  const [loading, setLoading] = useState(true);
  const [xpConfig, setXpConfig] = useState(DEFAULT_XP_CONFIG);
  const [levels, setLevels] = useState(DEFAULT_LEVELS);
  const [rewards, setRewards] = useState([]);
  const [originalConfig, setOriginalConfig] = useState(null);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rewardDialog, setRewardDialog] = useState(false);
  const [editReward, setEditReward] = useState(null);
  const [newReward, setNewReward] = useState({ level: 'Bronze', type: 'fuel_voucher', description: '', value: '' });
  const MAIN_COLOR = WEB_MAIN_COLOR(settings?.appCat);

  useEffect(() => {
    if (!firebase || !firebase.database) { setLoading(false); return; }
    const configRef = ref(firebase.database, 'league_config');
    console.log('Listening to league_config path');
    const unsubscribe = onValue(configRef, (snapshot) => {
      const val = snapshot.val();
      console.log('league_config value from Firebase:', val);
      if (val) {
        if (val.xpConfig) {
          console.log('xpConfig from Firebase:', val.xpConfig);
          setXpConfig(val.xpConfig);
        }
        if (val.levels) {
          console.log('levels from Firebase:', val.levels);
          setLevels(val.levels);
        }
        if (val.rewards) {
          console.log('rewards from Firebase:', val.rewards);
          setRewards(val.rewards);
        }
        setOriginalConfig({
          xpConfig: val.xpConfig,
          levels: val.levels,
          rewards: val.rewards
        });
      } else {
        console.log('No league_config found in Firebase, using defaults');
      }
      setLoading(false);
    }, (error) => {
      console.error('Error reading league_config:', error);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [firebase]);

  const saveConfig = async () => {
    if (!firebase || !firebase.database) {
      console.error('Firebase database not available');
      return;
    }
    
    console.log('Saving config:', { xpConfig, levels, rewards });
    setSaving(true);
    try {
      const configRef = ref(firebase.database, 'league_config');
      await set(configRef, {
        xpConfig,
        levels,
        rewards
      });
      console.log('League settings saved successfully');
      setOriginalConfig({ xpConfig, levels, rewards });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving league settings:', error);
      alert('Failed to save settings. Please check console for details.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddReward = () => {
    if (editReward !== null) {
      const updated = [...rewards];
      updated[editReward] = newReward;
      setRewards(updated);
    } else {
      setRewards([...rewards, newReward]);
    }
    setRewardDialog(false);
    setNewReward({ level: 'Bronze', type: 'fuel_voucher', description: '', value: '' });
    setEditReward(null);
  };

  const handleDeleteReward = (index) => {
    setRewards(rewards.filter((_, i) => i !== index));
  };

  const handleEditReward = (index) => {
    setNewReward(rewards[index]);
    setEditReward(index);
    setRewardDialog(true);
  };

  if (loading) return <CircularLoading />;

  return (
    <Box sx={{ p: 3 }}>
      {saved && <Alert severity="success" sx={{ mb: 2 }}>League settings saved successfully!</Alert>}

      {/* XP Configuration */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Star sx={{ color: MAIN_COLOR, mr: 1 }} />
            <Typography variant="h6" sx={{ fontFamily: FONT_FAMILY, fontWeight: 'bold' }}>
              XP Earning Rules
            </Typography>
            <Box sx={{ ml: 'auto' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={xpConfig.enabled}
                    onChange={(e) => setXpConfig({ ...xpConfig, enabled: e.target.checked })}
                    color="primary"
                  />
                }
                label="League System Enabled"
              />
            </Box>
          </Box>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="XP per hour online"
                type="number"
                value={xpConfig.onlinePerHour}
                onChange={(e) => setXpConfig({ ...xpConfig, onlinePerHour: parseInt(e.target.value) || 0 })}
                helperText="Earned for staying online"
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="XP per ride completed"
                type="number"
                value={xpConfig.rideComplete}
                onChange={(e) => setXpConfig({ ...xpConfig, rideComplete: parseInt(e.target.value) || 0 })}
                helperText="Earned when ride is completed"
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Accept rate threshold (%)"
                type="number"
                value={xpConfig.acceptRateBonus.threshold}
                onChange={(e) => setXpConfig({ ...xpConfig, acceptRateBonus: { ...xpConfig.acceptRateBonus, threshold: parseInt(e.target.value) || 0 } })}
                helperText="Minimum acceptance rate"
                InputProps={{ inputProps: { min: 0, max: 100 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Accept rate bonus XP"
                type="number"
                value={xpConfig.acceptRateBonus.xp}
                onChange={(e) => setXpConfig({ ...xpConfig, acceptRateBonus: { ...xpConfig.acceptRateBonus, xp: parseInt(e.target.value) || 0 } })}
                helperText="Bonus XP if rate above threshold"
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="XP per 5-star rating"
                type="number"
                value={xpConfig.fiveStarRating || 5}
                onChange={(e) => setXpConfig({ ...xpConfig, fiveStarRating: parseInt(e.target.value) || 0 })}
                helperText="Earned for each 5-star review"
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Levels Configuration */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <EmojiEvents sx={{ color: MAIN_COLOR, mr: 1 }} />
            <Typography variant="h6" sx={{ fontFamily: FONT_FAMILY, fontWeight: 'bold' }}>
              League Levels
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            {levels.map((level, idx) => (
              <Grid item xs={12} sm={6} md={4} lg={2.4} key={idx}>
                <Card variant="outlined" sx={{ p: 2, textAlign: 'center', borderColor: level.color, borderWidth: 2 }}>
                  <Typography variant="h4">{level.icon}</Typography>
                  <TextField
                    fullWidth
                    size="small"
                    label="Level Name"
                    value={level.name}
                    onChange={(e) => {
                      const updated = [...levels];
                      updated[idx].name = e.target.value;
                      setLevels(updated);
                    }}
                    sx={{ mt: 1, mb: 1 }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Min XP Required"
                    type="number"
                    value={level.minXP}
                    onChange={(e) => {
                      const updated = [...levels];
                      updated[idx].minXP = parseInt(e.target.value) || 0;
                      setLevels(updated);
                    }}
                    InputProps={{ inputProps: { min: 0 } }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Color (hex)"
                    value={level.color}
                    onChange={(e) => {
                      const updated = [...levels];
                      updated[idx].color = e.target.value;
                      setLevels(updated);
                    }}
                    sx={{ mt: 1 }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Icon/Emoji"
                    value={level.icon}
                    onChange={(e) => {
                      const updated = [...levels];
                      updated[idx].icon = e.target.value;
                      setLevels(updated);
                    }}
                    sx={{ mt: 1 }}
                  />
                </Card>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Rewards per Level */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <EmojiEvents sx={{ color: MAIN_COLOR, mr: 1 }} />
            <Typography variant="h6" sx={{ fontFamily: FONT_FAMILY, fontWeight: 'bold' }}>
              Level Rewards
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              sx={{ ml: 'auto', backgroundColor: MAIN_COLOR }}
              onClick={() => { setEditReward(null); setNewReward({ level: 'Bronze', type: 'fuel_voucher', description: '', value: '' }); setRewardDialog(true); }}
            >
              Add Reward
            </Button>
          </Box>
          <Divider sx={{ mb: 2 }} />

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell><strong>Level</strong></TableCell>
                  <TableCell><strong>Reward Type</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell><strong>Value</strong></TableCell>
                  <TableCell align="right"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rewards.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: '#999' }}>
                      No rewards configured yet. Click "Add Reward" to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  rewards.map((reward, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell>
                        <Chip
                          label={reward.level}
                          size="small"
                          sx={{ backgroundColor: levels.find(l => l.name === reward.level)?.color + '40' || '#eee' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip label={REWARD_TYPES.find(r => r.value === reward.type)?.label || reward.type} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>{reward.description}</TableCell>
                      <TableCell>{reward.value}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleEditReward(idx)}>
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteReward(idx)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Save Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
        {originalConfig && (
          <Typography 
            variant="body2" 
            sx={{ 
              color: JSON.stringify(originalConfig) === JSON.stringify({ xpConfig, levels, rewards }) ? '#999' : '#f59e0b',
              mr: 2 
            }}
          >
            {JSON.stringify(originalConfig) === JSON.stringify({ xpConfig, levels, rewards }) ? 'No changes' : 'Unsaved changes'}
          </Typography>
        )}
        <Button
          variant="contained"
          size="large"
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
          sx={{ backgroundColor: MAIN_COLOR, px: 4 }}
          onClick={saveConfig}
          disabled={!settings?.AllowCriticalEditsAdmin || saving}
        >
          {saving ? 'Saving...' : 'Save League Settings'}
        </Button>
      </Box>

      {/* Add/Edit Reward Dialog */}
      <Dialog open={rewardDialog} onClose={() => setRewardDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editReward !== null ? 'Edit Reward' : 'Add New Reward'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Level</InputLabel>
                <Select
                  value={newReward.level}
                  label="Level"
                  onChange={(e) => setNewReward({ ...newReward, level: e.target.value })}
                >
                  {levels.map((level, idx) => (
                    <MenuItem key={idx} value={level.name}>{level.icon} {level.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Reward Type</InputLabel>
                <Select
                  value={newReward.type}
                  label="Reward Type"
                  onChange={(e) => setNewReward({ ...newReward, type: e.target.value })}
                >
                  {REWARD_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={newReward.description}
                onChange={(e) => setNewReward({ ...newReward, description: e.target.value })}
                placeholder="e.g., ₱500 fuel voucher, 1 day free commission"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Value (amount or details)"
                value={newReward.value}
                onChange={(e) => setNewReward({ ...newReward, value: e.target.value })}
                placeholder="e.g., 500, 1 day, Priority for 7 days"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRewardDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddReward} sx={{ backgroundColor: MAIN_COLOR }}>
            {editReward !== null ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
